import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, API_BASE } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { TableIndexCell, TableIndexHead } from '@/components/ui/table-index';
import { rowSerial } from '@/lib/row-serial';
import { DatePicker } from '@/components/ui/date-picker';
import { Pagination } from '@/components/ui/pagination';
import { formatCurrency, formatNumber } from '@/lib/utils';
import { validateDateRange } from '@/lib/validation';
import {
  UNIFIED_REPORT_COLUMNS,
  UNIFIED_REPORT_COLUMN_KEYS,
  UNIFIED_COLUMN_GROUPS,
  UnifiedColumnGroup,
} from '@shared/custom-reports-contract';
import {
  SlidersHorizontal,
  Eye,
  FileSpreadsheet,
  Printer,
  X,
  RotateCcw,
  CheckSquare,
  Square,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  AlertCircle,
  FileText,
  ChevronDown,
  ChevronUp,
  TrendingUp,
} from 'lucide-react';
import './print-report.css';

const sanitizeSelectedColumns = (columns: readonly string[]): string[] => {
  const seen = new Set<string>();
  return columns.filter((key) => {
    if (!UNIFIED_REPORT_COLUMN_KEYS.has(key) || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const DEFAULT_COLUMNS = sanitizeSelectedColumns(
  UNIFIED_REPORT_COLUMNS
    .filter((column) => column.defaultSelected)
    .map((column) => column.key)
);

interface FilterState {
  projectId: string;
  dateFrom: string;
  dateTo: string;
  blockId: string;
  activityId: string;
  workerCategoryId: string;
  skillLevel: string;
  shiftId: string;
  requirementStatus: string;
  managerDecision: string;
  clusterDecision: string;
  actualStatus: string;
  recordType: string;
}

const INITIAL_FILTERS: FilterState = {
  projectId: '',
  dateFrom: '',
  dateTo: '',
  blockId: '',
  activityId: '',
  workerCategoryId: '',
  skillLevel: '',
  shiftId: '',
  requirementStatus: '',
  managerDecision: '',
  clusterDecision: '',
  actualStatus: '',
  recordType: '',
};

interface ReportConfig {
  selectedColumns: string[];
  filters: FilterState;
  orderBy: string;
  orderDir: 'asc' | 'desc';
}

export default function CustomReportBuilderPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const canExport = hasPermission('reports:export');

  // Report configuration state
  const [selectedColumns, setSelectedColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const effectiveSelectedColumns = useMemo(
    () => sanitizeSelectedColumns(selectedColumns),
    [selectedColumns]
  );
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [columnSearch, setColumnSearch] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Record<UnifiedColumnGroup, boolean>>({
    common: false,
    requirement: false,
    actual: false,
  });
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);

  // Visible columns for Section 2. Search and selected-only filters affect the
  // column picker only; they never change the current selection.
  const visibleColumns = useMemo(() => {
    const query = columnSearch.trim().toLowerCase();
    return UNIFIED_REPORT_COLUMNS.filter((col) => {
      if (showSelectedOnly && !effectiveSelectedColumns.includes(col.key)) return false;
      if (!query) return true;
      return (
        col.label.toLowerCase().includes(query) ||
        col.key.toLowerCase().includes(query)
      );
    });
  }, [showSelectedOnly, columnSearch, effectiveSelectedColumns]);

  const visibleColumnsByGroup = useMemo(
    () =>
      UNIFIED_COLUMN_GROUPS.map((group) => ({
        ...group,
        columns: visibleColumns.filter((col) => col.group === group.key),
        totalColumns: UNIFIED_REPORT_COLUMNS.filter((col) => col.group === group.key),
      })),
    [visibleColumns]
  );

  // Sorting state for table column headers
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Preview execution state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(true);
  const [printAllRows, setPrintAllRows] = useState(false);

  // Preview card ref and scroll trigger
  const previewCardRef = useRef<HTMLDivElement>(null);
  const shouldScrollOnSuccessRef = useRef(false);

  // Applied config snapshot for preview query
  const [appliedConfig, setAppliedConfig] = useState<ReportConfig>({
    selectedColumns: DEFAULT_COLUMNS,
    filters: INITIAL_FILTERS,
    orderBy: '',
    orderDir: 'asc',
  });

  const toggleColumnGroup = (group: UnifiedColumnGroup) => {
    setCollapsedGroups((previous) => ({
      ...previous,
      [group]: !previous[group],
    }));
  };

  // Handle table header sorting toggle (none -> asc -> desc -> none)
  const handleToggleSort = (colKey: string) => {
    setPage(1);
    if (sortKey !== colKey) {
      setSortKey(colKey);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
      setSortDir('asc');
    }
  };

  // Column selection helpers
  const handleSelectAllColumns = () => {
    setSelectedColumns(UNIFIED_REPORT_COLUMNS.map((c) => c.key));
    setValidationError(null);
  };

  const handleClearColumns = () => {
    setSelectedColumns([]);
  };

  const toggleColumn = (key: string) => {
    if (!UNIFIED_REPORT_COLUMN_KEYS.has(key)) return;
    setSelectedColumns((prev) =>
      sanitizeSelectedColumns(
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      )
    );
    setValidationError(null);
  };

  // Dropdown options queries
  const { data: projects } = useQuery({
    queryKey: ['projects-list'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/projects?availability=active');
      return r.data ?? [];
    },
  });

  const { data: blocks } = useQuery({
    queryKey: ['report-blocks', filters.projectId],
    queryFn: async () => {
      const q = filters.projectId ? `?projectId=${encodeURIComponent(filters.projectId)}` : '';
      const r = await api.get<{ data: any[] }>(`/blocks/all${q}`);
      return r.data ?? [];
    },
  });

  const { data: activities } = useQuery({
    queryKey: ['report-activities', filters.projectId, filters.blockId],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (filters.projectId) q.set('projectId', filters.projectId);
      if (filters.blockId) q.set('blockId', filters.blockId);
      const r = await api.get<{ data: any[] }>(`/activities/all?${q.toString()}`);
      return r.data ?? [];
    },
  });

  const { data: workerCategories } = useQuery({
    queryKey: ['labour-categories'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/labour-categories');
      return r.data ?? [];
    },
  });

  const { data: shifts } = useQuery({
    queryKey: ['shifts-list'],
    queryFn: async () => {
      const r = await api.get<{ data: any[] }>('/shifts');
      return r.data ?? [];
    },
  });

  // Build query string helper
  const buildQueryString = (cfg: ReportConfig, format = 'json') => {
    const params = new URLSearchParams();
    params.set('columns', sanitizeSelectedColumns(cfg.selectedColumns).join(','));
    if (cfg.filters.projectId) params.set('projectId', cfg.filters.projectId);
    if (cfg.filters.dateFrom) params.set('dateFrom', cfg.filters.dateFrom);
    if (cfg.filters.dateTo) params.set('dateTo', cfg.filters.dateTo);
    if (cfg.filters.blockId) params.set('blockId', cfg.filters.blockId);
    if (cfg.filters.activityId) params.set('activityId', cfg.filters.activityId);
    if (cfg.filters.workerCategoryId) params.set('workerCategoryId', cfg.filters.workerCategoryId);
    if (cfg.filters.skillLevel) params.set('skillLevel', cfg.filters.skillLevel);
    if (cfg.filters.shiftId) params.set('shiftId', cfg.filters.shiftId);
    if (cfg.filters.requirementStatus) params.set('requirementStatus', cfg.filters.requirementStatus);
    if (cfg.filters.actualStatus) params.set('actualStatus', cfg.filters.actualStatus);
    if (cfg.filters.managerDecision) params.set('managerDecision', cfg.filters.managerDecision);
    if (cfg.filters.clusterDecision) params.set('clusterDecision', cfg.filters.clusterDecision);
    if (cfg.filters.recordType) params.set('recordType', cfg.filters.recordType);

    if (cfg.orderBy) params.set('orderBy', cfg.orderBy);
    if (cfg.orderDir) params.set('orderDir', cfg.orderDir);
    params.set('format', format);

    return params.toString();
  };

  // Validate configuration before preview or export
  const validateConfig = (): boolean => {
    if (effectiveSelectedColumns.length === 0) {
      setValidationError('Please select at least one column to display.');
      return false;
    }
    if (filters.dateFrom && filters.dateTo) {
      const range = validateDateRange(filters.dateFrom, filters.dateTo);
      if (!range.valid) {
        setValidationError(range.startError || range.endError || 'Start Date cannot be after End Date.');
        return false;
      }
    }
    setValidationError(null);
    return true;
  };

  // Preview action
  const handleApplyPreview = () => {
    if (!validateConfig()) return;
    shouldScrollOnSuccessRef.current = true;
    const newConfig: ReportConfig = {
      selectedColumns: effectiveSelectedColumns,
      filters: { ...filters },
      orderBy: sortKey || '',
      orderDir: sortDir,
    };
    setPage(1);
    if (JSON.stringify(newConfig) === JSON.stringify(appliedConfig)) {
      const p = refetch();
      if (p && typeof p.then === 'function') {
        p.then((res) => {
          if (shouldScrollOnSuccessRef.current) {
            shouldScrollOnSuccessRef.current = false;
            if (res?.data && (res.data.rows?.length ?? 0) > 0) {
              previewCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }
        });
      }
    } else {
      setAppliedConfig(newConfig);
    }
    addToast('Report preview updated', 'info');
  };

  // Reset to default action
  const handleReset = () => {
    setFilters(INITIAL_FILTERS);
    setSortKey(null);
    setSortDir('asc');
    setSelectedColumns(DEFAULT_COLUMNS);
    setShowSelectedOnly(false);
    setColumnSearch('');
    setCollapsedGroups({ common: false, requirement: false, actual: false });
    setValidationError(null);
    setPage(1);
    setAppliedConfig({
      selectedColumns: DEFAULT_COLUMNS,
      filters: INITIAL_FILTERS,
      orderBy: '',
      orderDir: 'asc',
    });
  };

  // Preview Query
  const {
    data: reportResult,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['custom-report-preview', appliedConfig],
    queryFn: async (context) => {
      const signal = context?.signal;
      const q = buildQueryString(appliedConfig, 'json');
      const r = await api.get<{
        data: {
          reportType: string;
          columns: { key: string; label: string; type: string; totalable?: boolean }[];
          rows: Record<string, any>[];
          totals: Record<string, number>;
          totalRecords: number;
        };
      }>(`/reports/custom-builder?${q}`, signal ? { signal } : undefined);
      return r.data;
    },
    enabled: appliedConfig.selectedColumns.length > 0,
  });

  // Selected project resolution for report heading
  const selectedProject = useMemo(() => {
    if (!appliedConfig.filters.projectId) return null;
    return projects?.find((p) => p.id === appliedConfig.filters.projectId) ?? null;
  }, [projects, appliedConfig.filters.projectId]);

  const selectedProjectName = selectedProject?.name;

  // Smooth scroll to preview results after successful update preview
  useEffect(() => {
    if (shouldScrollOnSuccessRef.current && !isLoading) {
      shouldScrollOnSuccessRef.current = false;
      if (!isError && reportResult && (reportResult.rows?.length ?? 0) > 0) {
        previewCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [isLoading, isError, reportResult]);

  // Sort preview rows according to active column header sort
  const sortedRows = useMemo(() => {
    const rows = reportResult?.rows ?? [];
    if (rows.length === 0 || !sortKey) return rows;

    const colDef =
      reportResult?.columns?.find((c) => c.key === sortKey) ||
      UNIFIED_REPORT_COLUMNS.find((c) => c.key === sortKey);
    const colType = colDef?.type || 'string';
    const mult = sortDir === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      // Missing / null values consistently placed at the end
      const aMissing =
        av === null || av === undefined || av === '' || av === '—' || av === '-' || av === 'â€”';
      const bMissing =
        bv === null || bv === undefined || bv === '' || bv === '—' || bv === '-' || bv === 'â€”';
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;

      let cmp = 0;
      if (colType === 'number' || colType === 'currency') {
        const an = Number(av);
        const bn = Number(bv);
        cmp = (isNaN(an) ? 0 : an) - (isNaN(bn) ? 0 : bn);
      } else if (colType === 'date') {
        const at = new Date(av).getTime();
        const bt = new Date(bv).getTime();
        if (!isNaN(at) && !isNaN(bt)) {
          cmp = at - bt;
        } else {
          cmp = String(av).localeCompare(String(bv));
        }
      } else {
        // String, status, text: case-insensitive with natural number support
        cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (cmp !== 0) return cmp * mult;

      // Stable secondary tie-breaker
      const aTie = String(a.row_id ?? a.id ?? '');
      const bTie = String(b.row_id ?? b.id ?? '');
      return aTie.localeCompare(bTie);
    });
  }, [reportResult?.rows, reportResult?.columns, sortKey, sortDir]);

  // Client-side pagination over sorted rows. During PDF printing we render every
  // matching row so a paginated preview never truncates the exported document.
  const allRows = sortedRows;
  const paginatedRows = useMemo(() => {
    if (printAllRows) return allRows;
    const start = (page - 1) * pageSize;
    return allRows.slice(start, start + pageSize);
  }, [allRows, page, pageSize, printAllRows]);

  // Export Excel action
  const handleExportExcel = async () => {
    if (!validateConfig()) return;
    setExportingExcel(true);
    try {
      const q = buildQueryString(appliedConfig, 'excel');

      const token = sessionStorage.getItem('access_token');
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/reports/custom-builder?${q}`, {
        headers,
        credentials: 'include',
      });

      if (!res.ok) {
        let msg = `Export failed (${res.status})`;
        try {
          const body = await res.json();
          msg = body.error || msg;
        } catch { /* ignore */ }
        throw new Error(msg);
      }

      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match?.[1] || 'custom_unified_report.csv';

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);

      addToast('Report exported to Excel successfully', 'success');
    } catch (err: any) {
      addToast(err.message || 'Export Excel failed', 'error');
    } finally {
      setExportingExcel(false);
    }
  };

  // Export PDF action (triggers clean styled print document)
  const handleExportPdf = () => {
    if (!validateConfig()) return;
    if (allRows.length > pageSize) {
      setPrintAllRows(true);
      return;
    }
    window.print();
  };

  // Render every row before opening the print dialog, then restore pagination.
  useEffect(() => {
    if (!printAllRows) return;
    const timer = window.setTimeout(() => {
      window.print();
      setPrintAllRows(false);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [printAllRows]);

  // Cell renderer helper
  const renderCellContent = (colKey: string, val: any, type: string) => {
    if (val === null || val === undefined || val === '' || val === '—' || val === '-' || val === 'â€”') return '-';
    const cleanVal = typeof val === 'string' ? val.replace(/â€”|[\u2013\u2014]/g, '-').trim() : val;
    if (cleanVal === '-' || cleanVal === '') return '-';

    if (type === 'currency') {
      return formatCurrency(Number(cleanVal) || 0);
    }
    if (type === 'number') {
      return formatNumber(Number(cleanVal) || 0);
    }
    if (colKey === 'priority') {
      const p = String(cleanVal).toUpperCase();
      const style =
        p === 'URGENT'
          ? 'bg-rose-50 text-rose-700 border-rose-200 font-semibold'
          : p === 'HIGH'
          ? 'bg-amber-50 text-amber-700 border-amber-200'
          : p === 'LOW'
          ? 'bg-blue-50 text-blue-700 border-blue-200'
          : 'bg-slate-100 text-slate-700 border-slate-200';
      return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${style}`}>
          {p}
        </span>
      );
    }
    if (type === 'status' || type === 'badge') {
      const str = String(cleanVal);
      const isApproved = ['APPROVED', 'APPROVE', 'On Budget', 'Manager Approved'].includes(str);
      const isRejected = ['REJECTED', 'REJECT', 'REJECTED_BY_MANAGER', 'REJECTED_BY_CLUSTER_HEAD', 'Over Budget'].includes(str);
      const isPending = ['PENDING', 'PENDING_MANAGER_APPROVAL', 'MANAGER_APPROVED', 'Under Budget'].includes(str);

      const badgeStyle = isApproved
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : isRejected
        ? 'bg-red-50 text-red-700 border-red-200'
        : isPending
        ? 'bg-amber-50 text-amber-700 border-amber-200'
        : 'bg-slate-100 text-slate-700 border-slate-200';

      return (
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${badgeStyle}`}>
          {str.replace(/_/g, ' ')}
        </span>
      );
    }
    if (colKey === 'skill_level') {
      const s = String(cleanVal).toUpperCase();
      if (s === 'SEMI_SKILLED') return 'Semi-Skilled';
      if (s === 'SKILLED') return 'Skilled';
      if (s === 'UNSKILLED') return 'Unskilled';
      return String(cleanVal).replace(/_/g, ' ');
    }
    return String(cleanVal);
  };

  const canView = hasPermission('reports:view');
  if (!canView) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <h2 className="text-lg font-semibold text-primary mb-1">Permission Denied</h2>
        <p className="text-sm">You do not have permission to view reports.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── 1. Page Header & Action Bar ── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-6 w-6 text-brand-600" />
            <h1 className="text-2xl font-heading font-bold text-primary">Custom Report Builder</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            Single cohesive workspace to configure, filter, preview, and export customized labour reports
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <a
            href="/reports/analytics"
            className="inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 bg-slate-900 text-white hover:bg-slate-800 focus-visible:ring-slate-500 h-10 px-3.5 py-2 gap-1.5 shadow-sm"
            aria-label="Labour Analytics Dashboard"
          >
            <TrendingUp size={15} className="text-teal-400" />
            Analytics Dashboard
          </a>

          <Button
            onClick={handleApplyPreview}
            disabled={isLoading}
            className="gap-1.5"
            aria-label="Preview Report"
          >
            {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
            {isLoading ? 'Updating...' : 'Preview'}
          </Button>

          <Button
            variant="outline"
            onClick={handleExportExcel}
            disabled={exportingExcel || !canExport || (reportResult?.rows?.length === 0)}
            className="gap-1.5"
            aria-label="Export Excel"
            title={!canExport ? 'Export permission required' : 'Export to Excel (CSV)'}
          >
            {exportingExcel ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
            Export Excel
          </Button>

          <Button
            variant="outline"
            onClick={handleExportPdf}
            disabled={!reportResult?.rows || reportResult.rows.length === 0}
            className="gap-1.5"
            aria-label="Export PDF"
            title="Export report as PDF / Print document"
          >
            <Printer size={15} />
            Export PDF
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowSettings((s) => !s)}
            className="gap-1.5 text-secondary"
            aria-label={showSettings ? 'Hide Report Settings' : 'Show Report Settings'}
            title={showSettings ? 'Collapse configuration settings to focus on report preview' : 'Expand configuration settings'}
          >
            <SlidersHorizontal size={15} />
            {showSettings ? 'Hide Settings' : 'Show Settings'}
          </Button>

          <Button
            variant="ghost"
            onClick={() => navigate('/reports')}
            className="gap-1.5 text-muted-foreground hover:text-primary"
            aria-label="Close Custom Report Builder"
          >
            <X size={16} />
            Close
          </Button>
        </div>
      </div>

      {/* ── Configuration Workspace (Collapsible) ── */}
      {showSettings && (
        <div className="space-y-4 print:hidden">
          {/* ── Top Row: Unified Filters ── */}
          <Card className="border-default shadow-sm">
        <CardContent className="p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-5 items-start">
            <div className="space-y-2.5">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-secondary">
                  1. Filters
                </Label>
                <span className="text-xs text-muted-foreground hidden sm:inline">
                  One set of filters across requirement and actual labour records
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2.5">
                {/* Project Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-project" className="text-xs text-muted-foreground">Project</Label>
                  <Select
                    value={filters.projectId}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, projectId: v === '__all__' ? '' : v, blockId: '', activityId: '' }))
                    }
                  >
                    <SelectTrigger id="filter-project" className="w-full">
                      <SelectValue placeholder="All Projects" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Permitted Projects</SelectItem>
                      {projects?.map((p) => (
                        <SelectItem key={p.id ?? p.project_id} value={p.id ?? p.project_id}>
                          {p.name ?? p.project_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div className="space-y-1">
                  <Label htmlFor="filter-date-from" className="text-xs text-muted-foreground">Start Date</Label>
                  <DatePicker
                    id="filter-date-from"
                    value={filters.dateFrom}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({ ...f, dateFrom: v }));
                      setValidationError(null);
                    }}
                    className="w-full"
                    placeholder="From"
                  />
                </div>

                {/* Date To */}
                <div className="space-y-1">
                  <Label htmlFor="filter-date-to" className="text-xs text-muted-foreground">End Date</Label>
                  <DatePicker
                    id="filter-date-to"
                    value={filters.dateTo}
                    onChange={(e) => {
                      const v = e.target.value;
                      setFilters((f) => ({ ...f, dateTo: v }));
                      setValidationError(null);
                    }}
                    className="w-full"
                    placeholder="To"
                  />
                </div>

                {/* Block Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-block" className="text-xs text-muted-foreground">Block</Label>
                  <Select
                    value={filters.blockId}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, blockId: v === '__all__' ? '' : v, activityId: '' }))
                    }
                  >
                    <SelectTrigger id="filter-block" className="w-full">
                      <SelectValue placeholder="All Blocks" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Blocks</SelectItem>
                      {blocks?.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Activity Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-activity" className="text-xs text-muted-foreground">Activity</Label>
                  <Select
                    value={filters.activityId}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, activityId: v === '__all__' ? '' : v }))
                    }
                  >
                    <SelectTrigger id="filter-activity" className="w-full">
                      <SelectValue placeholder="All Activities" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Activities</SelectItem>
                      {activities?.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Worker Category & Skill Level Filters */}
                <div className="space-y-1">
                  <Label htmlFor="filter-category" className="text-xs text-muted-foreground">Worker Category</Label>
                      <Select
                        value={filters.workerCategoryId}
                        onValueChange={(v) =>
                          setFilters((f) => ({ ...f, workerCategoryId: v === '__all__' ? '' : v }))
                        }
                      >
                        <SelectTrigger id="filter-category" className="w-full">
                          <SelectValue placeholder="All Categories" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Categories</SelectItem>
                          {workerCategories?.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="filter-skill" className="text-xs text-muted-foreground">Skill Level</Label>
                      <Select
                        value={filters.skillLevel}
                        onValueChange={(v) =>
                          setFilters((f) => ({ ...f, skillLevel: v === '__all__' ? '' : v }))
                        }
                      >
                        <SelectTrigger id="filter-skill" className="w-full">
                          <SelectValue placeholder="All Skills" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Skills</SelectItem>
                          <SelectItem value="SKILLED">Skilled</SelectItem>
                          <SelectItem value="SEMI_SKILLED">Semi-Skilled</SelectItem>
                          <SelectItem value="UNSKILLED">Unskilled</SelectItem>
                        </SelectContent>
                      </Select>
                </div>

                {/* Shift Filter (Applies to requirement records) */}
                <div className="space-y-1">
                  <Label htmlFor="filter-shift" className="text-xs text-muted-foreground">Shift</Label>
                    <Select
                      value={filters.shiftId}
                      onValueChange={(v) =>
                        setFilters((f) => ({ ...f, shiftId: v === '__all__' ? '' : v }))
                      }
                    >
                      <SelectTrigger id="filter-shift" className="w-full">
                        <SelectValue placeholder="All Shifts" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All Shifts</SelectItem>
                        {shifts?.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                </div>

                {/* Requirement Status Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-requirement-status" className="text-xs text-muted-foreground">Requirement Status</Label>
                  <Select
                    value={filters.requirementStatus}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, requirementStatus: v === '__all__' ? '' : v }))
                    }
                  >
                    <SelectTrigger id="filter-requirement-status" className="w-full">
                      <SelectValue placeholder="All Requirement Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Requirement Statuses</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING_MANAGER_APPROVAL">Pending Manager Approval</SelectItem>
                      <SelectItem value="MANAGER_APPROVED">Manager Approved</SelectItem>
                      <SelectItem value="REJECTED_BY_MANAGER">Rejected by Manager</SelectItem>
                      <SelectItem value="APPROVED">Approved (Final)</SelectItem>
                      <SelectItem value="REJECTED_BY_CLUSTER_HEAD">Rejected by Cluster Head</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* PM Approval Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-manager-decision" className="text-xs text-muted-foreground">PM Approval Status</Label>
                  <Select
                    value={filters.managerDecision}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, managerDecision: v === '__all__' ? '' : v }))
                    }
                  >
                    <SelectTrigger id="filter-manager-decision" className="w-full">
                      <SelectValue placeholder="All PM Approvals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All PM Approvals</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="REJECTED">Rejected</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* CH Approval Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-cluster-decision" className="text-xs text-muted-foreground">CH Approval Status</Label>
                  <Select
                    value={filters.clusterDecision}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, clusterDecision: v === '__all__' ? '' : v }))
                    }
                  >
                    <SelectTrigger id="filter-cluster-decision" className="w-full">
                      <SelectValue placeholder="All CH Approvals" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All CH Approvals</SelectItem>
                      <SelectItem value="APPROVED">Approved</SelectItem>
                      <SelectItem value="REJECTED">Rejected</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Actual Labour Status Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-actual-status" className="text-xs text-muted-foreground">Actual Labour Status</Label>
                  <Select
                    value={filters.actualStatus}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, actualStatus: v === '__all__' ? '' : v }))
                    }
                  >
                    <SelectTrigger id="filter-actual-status" className="w-full">
                      <SelectValue placeholder="All Actual Labour Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Actual Labour Statuses</SelectItem>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING_MANAGER_APPROVAL">Pending Manager Approval</SelectItem>
                      <SelectItem value="MANAGER_APPROVED">Manager Approved</SelectItem>
                      <SelectItem value="REJECTED_BY_MANAGER">Rejected by Manager</SelectItem>
                      <SelectItem value="APPROVED">Approved (Final)</SelectItem>
                      <SelectItem value="REJECTED_BY_CLUSTER_HEAD">Rejected by Cluster Head</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Record Type Filter */}
                <div className="space-y-1">
                  <Label htmlFor="filter-record-type" className="text-xs text-muted-foreground">Record Type</Label>
                  <Select
                    value={filters.recordType}
                    onValueChange={(v) =>
                      setFilters((f) => ({ ...f, recordType: v === '__all__' ? '' : v }))
                    }
                  >
                    <SelectTrigger id="filter-record-type" className="w-full">
                      <SelectValue placeholder="All Records" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All Records</SelectItem>
                      <SelectItem value="Requirement">Requirements</SelectItem>
                      <SelectItem value="Actual Labour">Actual Labour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

          {/* ── Section 2: Select Columns (Full-Width Horizontal Section) ── */}
          <Card id="section-select-columns" className="border-default shadow-sm">
            <CardHeader className="p-2 sm:px-3 sm:py-2 border-b border-default bg-surface-secondary/20">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-semibold text-secondary">
                    2. Select Columns
                  </Label>
                  <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 border border-brand-200">
                    {effectiveSelectedColumns.length} of {UNIFIED_REPORT_COLUMNS.length} selected
                  </span>
                  <div className="relative">
                    <Input
                      value={columnSearch}
                      onChange={(event) => setColumnSearch(event.target.value)}
                      placeholder="Search columns..."
                      aria-label="Search columns"
                      className="h-7 w-44 text-xs pl-7 pr-7"
                    />
                    <SlidersHorizontal
                      size={12}
                      className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                    />
                    {columnSearch && (
                      <button
                        type="button"
                        onClick={() => setColumnSearch('')}
                        aria-label="Clear column search"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant={showSelectedOnly ? "secondary" : "outline"}
                    size="sm"
                    onClick={() => setShowSelectedOnly((v) => !v)}
                    className="h-7 text-xs gap-1 font-medium"
                    aria-label={showSelectedOnly ? "Show All Columns" : "Show Selected Only"}
                    aria-expanded={showSelectedOnly}
                  >
                    <SlidersHorizontal size={12} />
                    {showSelectedOnly ? "Show All Columns" : "Show Selected Only"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAllColumns}
                    className="h-7 text-xs gap-1"
                    aria-label="Select All"
                  >
                    <CheckSquare size={12} />
                    Select All
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleClearColumns}
                    className="h-7 text-xs gap-1"
                    aria-label="Clear"
                  >
                    <Square size={12} />
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-2.5 sm:p-3 space-y-1.5">
              {visibleColumns.length > 0 ? (
                <div className="space-y-1.5">
                  {visibleColumnsByGroup.map((group) => {
                    if (group.columns.length === 0) return null;
                    const collapsed = collapsedGroups[group.key];
                    const selectedInGroup = group.totalColumns.filter((column) =>
                      effectiveSelectedColumns.includes(column.key)
                    ).length;
                    return (
                      <div key={group.key} className="rounded-md border border-default/60 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => toggleColumnGroup(group.key)}
                          className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 bg-surface-secondary/30 text-left hover:bg-surface-secondary/50 transition-colors"
                          aria-expanded={!collapsed}
                          aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.label} columns`}
                        >
                          <span className="flex items-center gap-1.5 text-xs font-semibold text-secondary">
                            {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
                            {group.label}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {selectedInGroup} of {group.totalColumns.length} selected
                          </span>
                        </button>
                        {!collapsed && (
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-1.5 p-1.5">
                            {group.columns.map((col) => {
                              const isChecked = effectiveSelectedColumns.includes(col.key);
                              return (
                                <label
                                  key={col.key}
                                  className={`flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium transition-colors cursor-pointer select-none border ${
                                    isChecked
                                      ? 'bg-brand-50/70 text-brand-900 border-brand-200 shadow-xs'
                                      : 'text-muted-foreground hover:text-primary hover:bg-surface-secondary/60 border-default/60'
                                  }`}
                                  title={col.label}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleColumn(col.key)}
                                    className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 accent-blue-600 cursor-pointer shrink-0"
                                  />
                                  <span className="truncate" title={col.label}>
                                    {col.label}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="py-2.5 px-3 rounded-md bg-surface-secondary/30 border border-dashed border-default text-center">
                  <p className="text-xs text-muted-foreground">
                    No columns match the current search or selection view. Click{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setShowSelectedOnly(false);
                        setColumnSearch('');
                      }}
                      className="text-primary font-medium underline hover:text-primary/80"
                      aria-label="Reveal all columns"
                    >
                      Show All Columns
                    </button>{' '}
                    to choose fields for this report.
                  </p>
                </div>
              )}

              {effectiveSelectedColumns.length === 0 && (
                <p className="text-xs text-red-500 flex items-center gap-1 mt-1 px-1" role="alert">
                  <AlertCircle size={13} className="shrink-0" />
                  Please select at least one column to display in preview and export.
                </p>
              )}
            </CardContent>
          </Card>

          {/* ── Actions ── */}
          <Card className="border-default shadow-sm">
            <CardContent className="p-3.5 sm:p-4 space-y-3">
              {/* Validation Banner */}
              {validationError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700" role="alert">
                  <AlertCircle size={16} className="shrink-0" />
                  <span>{validationError}</span>
                </div>
              )}

              {/* Action Footer */}
              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  className="gap-1.5 text-xs text-muted-foreground"
                >
                  <RotateCcw size={13} />
                  Reset Configuration
                </Button>

                <Button
                  type="button"
                  onClick={handleApplyPreview}
                  disabled={isLoading}
                  className="gap-1.5"
                >
                  {isLoading ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
                  {isLoading ? 'Updating...' : 'Update Preview'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── 4. Single Report Preview Area ── */}
      <Card ref={previewCardRef} className="border-default shadow-sm print-report-sheet">
        <CardHeader className="p-4 sm:p-5 pb-3 flex flex-row items-center justify-between border-b border-default">
          <div>
            <CardTitle className="text-base font-semibold text-primary">
              Report Preview: Labour Requirements & Actual Labour — {selectedProjectName || 'All Permitted Projects'}
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              {reportResult?.totalRecords !== undefined
                ? `Total ${formatNumber(reportResult.totalRecords)} records found`
                : 'Showing configured report preview'}
            </p>
          </div>

          {/* Settings toggle & Items per page selector */}
          <div className="flex items-center gap-2 print:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings((s) => !s)}
              className="h-8 text-xs text-muted-foreground hover:text-primary gap-1"
              aria-label={showSettings ? 'Hide Report Settings' : 'Show Report Settings'}
              title={showSettings ? 'Collapse configuration to focus on report preview' : 'Expand configuration settings'}
            >
              {showSettings ? (
                <>
                  <ChevronUp size={14} />
                  Hide Settings
                </>
              ) : (
                <>
                  <ChevronDown size={14} />
                  Show Settings
                </>
              )}
            </Button>

            <div className="h-4 w-px bg-default/60 mx-0.5 hidden sm:block" />

            <span className="text-xs text-muted-foreground hidden sm:inline">Per page:</span>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => {
                setPageSize(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-8 w-20 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>

            <CardContent className="p-0">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
                  <p className="text-sm text-muted-foreground">Generating report data...</p>
                </div>
              ) : isError ? (
                <div className="flex flex-col items-center justify-center py-12 gap-3 text-center px-4">
                  <AlertCircle className="h-8 w-8 text-red-500" />
                  <p className="text-sm text-red-600">
                    {(error as any)?.message || 'Unable to load report data. Please verify your filters.'}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              ) : allRows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 gap-2 text-center px-4">
                  <FileText className="h-10 w-10 text-muted-foreground/50" />
                  <p className="text-base font-medium text-primary">No records match the selected criteria</p>
                  <p className="text-xs text-muted-foreground max-w-md">
                    Try widening your date range, choosing &ldquo;All Permitted Projects&rdquo;, or adjusting the filters.
                  </p>
                </div>
              ) : (
                <>
                  {/* Responsive scrollable table */}
                  <div className="overflow-x-auto">
                    <Table className="print-table min-w-full">
                      <TableHeader>
                        <TableRow className="bg-surface-secondary/40">
                          <TableIndexHead />
                          {reportResult?.columns.map((col) => {
                            const isSorted = sortKey === col.key;
                            return (
                              <TableHead
                                key={col.key}
                                onClick={() => handleToggleSort(col.key)}
                                className="whitespace-nowrap font-semibold text-secondary cursor-pointer hover:bg-surface-secondary/80 hover:text-primary transition-colors select-none group"
                                title={`Click to sort by ${col.label}${isSorted ? (sortDir === 'asc' ? ' (descending)' : ' (clear sort)') : ' (ascending)'}`}
                                aria-sort={isSorted ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                              >
                                <div className="inline-flex items-center gap-1.5">
                                  <span>{col.label}</span>
                                  <span className="inline-flex items-center text-muted-foreground group-hover:text-primary">
                                    {isSorted ? (
                                      sortDir === 'asc' ? (
                                        <ArrowUp size={13} className="text-brand-600 font-bold" />
                                      ) : (
                                        <ArrowDown size={13} className="text-brand-600 font-bold" />
                                      )
                                    ) : (
                                      <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-60 transition-opacity" />
                                    )}
                                  </span>
                                </div>
                              </TableHead>
                            );
                          })}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedRows.map((row, idx) => {
                          const absoluteIndex = printAllRows
                            ? idx
                            : (page - 1) * pageSize + idx;
                          return (
                            <TableRow key={row.row_id || idx} className="group hover:bg-hover/50">
                              <TableIndexCell value={absoluteIndex + 1} />
                              {reportResult?.columns.map((col) => (
                                <TableCell key={col.key} className="whitespace-nowrap text-sm text-primary">
                                  {renderCellContent(col.key, row[col.key], col.type)}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>

                      {/* Summary / Totals Row */}
                      {reportResult?.totals && Object.keys(reportResult.totals).length > 0 && (
                        <tfoot>
                          <TableRow className="bg-surface-secondary/80 font-bold border-t-2 border-default">
                            <TableCell className="text-center font-bold">
                              <span>Total</span>
                              <span className="block text-[10px] font-normal text-muted-foreground">
                                All {formatNumber(reportResult.totalRecords)} records
                              </span>
                            </TableCell>
                            {reportResult?.columns.map((col) => {
                              const hasTotal = col.totalable && reportResult.totals[col.key] !== undefined;
                              return (
                                <TableCell key={col.key} className="whitespace-nowrap font-semibold text-primary">
                                  {hasTotal
                                    ? renderCellContent(col.key, reportResult.totals[col.key], col.type)
                                    : ''}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        </tfoot>
                      )}
                    </Table>
                  </div>

                  {/* Pagination */}
                  {allRows.length > pageSize && (
                    <div className="print:hidden">
                      <Pagination
                        page={page}
                        pageSize={pageSize}
                        total={allRows.length}
                        onPageChange={setPage}
                      />
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
    </div>
  );
}
